import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Stack } from '@mui/material';

import { useCreateRateGroupRule } from '~/hooks/apis/cruises/useCreateRateGroupRule';
import { useGetRateGroupRecommendations } from '~/hooks/apis/cruises/useGetRateGroupRecommendations';
import { useGetUngroupedRateDetails } from '~/hooks/apis/cruises/useGetUngroupedRateDetails';

import { CreateRateGroupRuleBody } from '~/services/cruises/RateGroupRuleService';
import { RateByGroup, RateGroup, UngroupedRateWithDetails } from '~/services/cruises/RateGroupService';

import AcceptSuggestionModal from './AcceptSuggestionModal';
import RateCodeCard from './RateCodeCard';

interface Props {
  rates: Array<RateByGroup>;
  rateGroups: Array<RateGroup>;
  shouldFetchRateDetails: boolean;
  vendorId: string | undefined;
}

const recommendationConfidencePriority = {
  High: 3,
  Medium: 2,
  Low: 1,
};

function RateCodeList({ rates, rateGroups, shouldFetchRateDetails, vendorId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const {
    detailsByRateId,
    isLoadingByDepartureId,
    isErrorByDepartureId,
    isSettled: areDetailsSettled,
  } = useGetUngroupedRateDetails(vendorId, rates, shouldFetchRateDetails);
  const ratesWithDetails: Array<UngroupedRateWithDetails> = rates.flatMap((rate) => {
    const details = detailsByRateId.get(rate.id);
    return details ? [{ ...rate, ...details }] : [];
  });
  const recommendationsQuery = useGetRateGroupRecommendations(
    ratesWithDetails,
    shouldFetchRateDetails && areDetailsSettled,
  );
  const createRateGroupRule = useCreateRateGroupRule();
  const [selectedRate, setSelectedRate] = useState<RateByGroup | null>(null);
  const [selectedRateGroupId, setSelectedRateGroupId] = useState<string | null>(null);
  const recommendationsByRateId = useMemo(
    () => new Map(recommendationsQuery.data?.map(({ rateId, recommendation }) => [rateId, recommendation])),
    [recommendationsQuery.data],
  );
  const sortedRates = useMemo(
    () =>
      rates
        .map((rate, index) => ({ rate, index }))
        .sort((left, right) => {
          const leftRecommendation = recommendationsByRateId.get(left.rate.id);
          const rightRecommendation = recommendationsByRateId.get(right.rate.id);
          const leftPriority = leftRecommendation?.rateGroup
            ? recommendationConfidencePriority[leftRecommendation.confidence]
            : 0;
          const rightPriority = rightRecommendation?.rateGroup
            ? recommendationConfidencePriority[rightRecommendation.confidence]
            : 0;

          return rightPriority - leftPriority || left.index - right.index;
        })
        .map(({ rate }) => rate),
    [rates, recommendationsByRateId],
  );
  const selectedRecommendation = selectedRate ? recommendationsByRateId.get(selectedRate.id) : undefined;
  const closeSuggestionModal = () => {
    setSelectedRate(null);
    setSelectedRateGroupId(null);
  };
  const handleConfirmSuggestion = useCallback(
    async (
      rateGroupId: string,
      filterType: CreateRateGroupRuleBody['filters'][number]['filterType'],
      matchType: CreateRateGroupRuleBody['filters'][number]['matchType'],
      rateCodePattern: string,
    ) => {
      if (!selectedRate) {
        return;
      }

      try {
        await createRateGroupRule.mutateAsync({
          rateGroupId,
          body: {
            filters: [{ filterType, rateCodePattern, matchType }],
          },
        });
        enqueueSnackbar('Rate group rule created successfully', { variant: 'success' });
        closeSuggestionModal();
      } catch {
        // The modal renders the mutation error and keeps the selected rate for retry.
      }
    },
    [createRateGroupRule, enqueueSnackbar, selectedRate],
  );

  return (
    <>
      <Stack spacing={1}>
        {sortedRates.map((rate) => (
          <RateCodeCard
            key={rate.id}
            rate={rate}
            rateDetails={detailsByRateId.get(rate.id)}
            isDetailsLoading={Boolean(rate.departureId && isLoadingByDepartureId.get(rate.departureId))}
            isDetailsError={Boolean(rate.departureId && isErrorByDepartureId.get(rate.departureId))}
            recommendation={recommendationsByRateId.get(rate.id)}
            isRecommendationWaiting={shouldFetchRateDetails && !areDetailsSettled}
            isRecommendationLoading={shouldFetchRateDetails && areDetailsSettled && recommendationsQuery.isLoading}
            isRecommendationError={recommendationsQuery.isError}
            hasRateGroups={rateGroups.length > 0}
            onAcceptSuggestion={() => {
              setSelectedRate(rate);
              setSelectedRateGroupId(recommendationsByRateId.get(rate.id)?.rateGroup?.id ?? null);
            }}
            onChooseGroup={() => {
              setSelectedRate(rate);
              setSelectedRateGroupId(rateGroups[0]?.id ?? null);
            }}
          />
        ))}
      </Stack>
      {selectedRate && selectedRateGroupId && (
        <AcceptSuggestionModal
          isError={createRateGroupRule.isError}
          isLoading={createRateGroupRule.isPending}
          isOpen
          rateCode={selectedRate.code}
          rateDescription={selectedRate.rateDescription}
          rateGroups={rateGroups}
          suggestion={selectedRecommendation}
          suggestedRateGroupId={selectedRateGroupId}
          onClose={closeSuggestionModal}
          onConfirm={handleConfirmSuggestion}
        />
      )}
    </>
  );
}

export default RateCodeList;
